import {useDomainNameQuery} from "@/queries.generated";
import {UpdateCallQueueMutationVariables} from "./mutation.generated";
import {CallQueuesQuery, useCallQueuesQuery} from "./queries.generated";
import {useCallback} from "react";
import {DomainOutgoingCallBarrings, useDomainOutgoingCallBarringsQuery} from "@/hooks";

export type DataSource = {
    loading: boolean;
    domainId: number;
    domainName: string;
    data: UpdateCallQueueMutationVariables;
    refetch: () => Promise<UpdateCallQueueMutationVariables>;
    downloadTokens: Record<number, string>;
    domainOutgoingCallBarrings: DomainOutgoingCallBarrings;
};

const createData = (queryData: CallQueuesQuery["callQueues"][number]): UpdateCallQueueMutationVariables => ({
    id: queryData.id,
    name: queryData.name,
    sequentialTimeout: queryData.sequentialTimeout,
    huntingAlgorithmType: queryData.huntingAlgorithmType,
    maxQueueLength: queryData.maxQueueLength,
    maxWaitingTime: queryData.maxWaitingTime,
    outgoingCallBarringAdmin: queryData.outgoingCallBarringAdmin,
    outgoingCallBarringOperator: queryData.outgoingCallBarringOperator,
    acwLength: queryData.acwLength,
    hasAddressBook: queryData.hasAddressBook,
    queueFull: {
        id: queryData.media.queueFull?.id,
        description: queryData.media.queueFull?.description,
        file: undefined,
        unset: false,
    },
    queueTimeout: {
        id: queryData.media.queueTimeout?.id,
        description: queryData.media.queueTimeout?.description,
        file: undefined,
        unset: false,
    },
    waiting: {
        id: queryData.media.waiting?.id,
        description: queryData.media.waiting?.description,
        file: undefined,
        unset: false,
    },
});

export function useDataSource(domainId: number, groupId: number): DataSource {
    const domainNameQuery = useDomainNameQuery({
        variables: {id: domainId},
    });
    const query = useCallQueuesQuery({variables: {domainId, groupId}, fetchPolicy: "no-cache"});
    const domainCallBarringsQuery = useDomainOutgoingCallBarringsQuery(domainId);

    const refetch = useCallback(async () => {
        const {data} = await query.refetch();
        return createData(data.callQueues[0]);
    }, [query]);

    if (query.loading || domainNameQuery.loading || domainCallBarringsQuery.loading)
        return {
            loading: true,
            domainId,
            domainName: undefined,
            data: undefined,
            refetch,
            downloadTokens: {},
            domainOutgoingCallBarrings: undefined,
        };

    const data = query.data.callQueues[0];

    const downloadTokens: Record<number, string> = {};

    if (data.media.queueFull) {
        downloadTokens[data.media.queueFull.id] = data.media.queueFull.downloadToken;
    }
    if (data.media.queueTimeout) {
        downloadTokens[data.media.queueTimeout.id] = data.media.queueTimeout.downloadToken;
    }
    if (data.media.waiting) {
        downloadTokens[data.media.waiting.id] = data.media.waiting.downloadToken;
    }

    return {
        loading: false,
        domainId,
        domainName: domainNameQuery.data.domains.items[0].name,
        data: createData(data),
        refetch,
        downloadTokens,
        domainOutgoingCallBarrings: domainCallBarringsQuery.domainOutgoingCallBarrings,
    };
}
